import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncProblemOutlined from '@mui/icons-material/SyncProblemOutlined';
import {
  CustomTableHead,
  CustomTableHeadCell,
  CustomTableFoot,
  CustomTableRow,
} from '../../components/@extended/CustomTable';
import CustomTablePagination from '../../components/common/TablePagination';
import {
  useGetOnboardedEntitiesQuery,
  useGetEntityTypesQuery,
  useRequestVerificationMutation,
} from '../../store/slices/apiSlice';
import { useDispatch } from 'react-redux';
import { openMessage } from '../../store/slices/showMessageSlice';
import {
  active,
  blocked,
  pending,
  restricted,
  userVerifying,
  verificationRequired,
} from '../../constants/bankAccountStatus';

const DEFAULT_ENTITY_TYPES = ['SITE', 'CRO', 'SITE_MEMBER'];
const EXCLUDED_ENTITY_TYPES = ['SPONSOR', 'SUBJECT'];

const ALL_ENTITY_TYPE_FILTER = '__ALL__';

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 280,
    },
  },
};

const formatEntityTypeLabel = (type) => type.replace(/_/g, ' ');

const ACCOUNT_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  // { value: 'verification_required', label: 'Verification Required' },
  // { value: 'user_verifying', label: 'User Verifying' },
  // { value: 'restricted', label: 'Restricted' },
  // { value: 'blocked', label: 'Blocked' },
  { value: 'active', label: 'Active' },
];

const SORT_BY_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'updatedAt', label: 'Updated Date' },
  { value: 'email', label: 'Email' },
  { value: 'type', label: 'Entity Type' },
  { value: 'accountStatus', label: 'Account Status' },
];

const columns = [
  { id: 'name', label: 'Name', minWidth: 140, align: 'left' },
  { id: 'email', label: 'Email', minWidth: 180, align: 'left' },
  { id: 'type', label: 'Entity Type', minWidth: 110, align: 'center' },
  { id: 'accountStatus', label: 'Account Status', minWidth: 130, align: 'center' },
  // { id: 'externalMasterId', label: 'External Master ID', minWidth: 130, align: 'center' },
  { id: 'createdAt', label: 'Created', minWidth: 110, align: 'center' },
  { id: 'updatedAt', label: 'Updated', minWidth: 110, align: 'center' },
  // { id: 'action', label: '', minWidth: 60, align: 'center' },
];

const defaultFilters = {
  search: '',
  entityType: ALL_ENTITY_TYPE_FILTER,
  accountStatus: '',
  sortBy: 'createdAt',
  sortOrder: 'DESC',
  hasStripeCustomer: '',
  hasStripeAccount: '',
};

const getStatusColor = (status, theme) => {
  if (pending.includes(status) || verificationRequired.includes(status) || userVerifying.includes(status)) {
    return theme.palette.warning.main;
  }
  if (active.includes(status)) {
    return theme.palette.success.main;
  }
  if (restricted.includes(status) || blocked.includes(status)) {
    return theme.palette.error.main;
  }
  return theme.palette.text.secondary;
};

const formatStatusLabel = (status) => {
  if (!status) return '-';
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const OnboardedEntitiesDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 10 });
  const [filters, setFilters] = useState(defaultFilters);
  const [searchInput, setSearchInput] = useState('');
  const [requestVerification, { isLoading: verificationLoading }] = useRequestVerificationMutation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: entityTypesData, refetch: refetchEntityTypes } = useGetEntityTypesQuery(undefined, {
    skip: !open,
  });

  const entityTypeOptions = useMemo(() => {
    const fromApi = entityTypesData?.entityTypes ?? [];
    const merged = [...DEFAULT_ENTITY_TYPES, ...fromApi];
    return [...new Set(merged.map((type) => type?.trim()).filter(Boolean))]
      .filter((type) => !EXCLUDED_ENTITY_TYPES.includes(type))
      .sort((a, b) => a.localeCompare(b));
  }, [entityTypesData]);

  const queryParams = useMemo(() => {
    const params = {
      page: pagination.page + 1,
      limit: pagination.rowsPerPage,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    };
    if (filters.search) params.search = filters.search;
    if (filters.entityType && filters.entityType !== ALL_ENTITY_TYPE_FILTER) {
      params.entityType = filters.entityType;
    } else {
      const allowedTypes =
        entityTypeOptions.length > 0 ? entityTypeOptions : DEFAULT_ENTITY_TYPES;
      params.entityType = allowedTypes.join(',');
    }
    if (filters.accountStatus) params.accountStatus = filters.accountStatus;
    if (filters.hasStripeCustomer !== '') {
      params.hasStripeCustomer = filters.hasStripeCustomer === 'true';
    }
    if (filters.hasStripeAccount !== '') {
      params.hasStripeAccount = filters.hasStripeAccount === 'true';
    }
    return params;
  }, [pagination, filters, entityTypeOptions]);

  const { data, isLoading, isFetching, refetch } = useGetOnboardedEntitiesQuery(queryParams, {
    skip: !open,
  });

  const rows = useMemo(
    () =>
      (data?.entities ?? []).filter((row) => !EXCLUDED_ENTITY_TYPES.includes(row.type)),
    [data?.entities]
  );

  useEffect(() => {
    if (!open) {
      setPagination({ page: 0, rowsPerPage: 10 });
      setFilters(defaultFilters);
      setSearchInput('');
    }
  }, [open]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleRefresh = () => {
    refetch();
    refetchEntityTypes();
  };

  const handleRequestVerification = async (entityId) => {
    try {
      await requestVerification(entityId).unwrap();
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: 'Verification email sent successfully.',
        })
      );
      refetch();
    } catch (error) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: error?.data?.message || 'Failed to send verification email.',
        })
      );
    }
  };

  const canRequestVerification = (status) =>
    pending.includes(status) || verificationRequired.includes(status);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        Onboarded Entities
        <Box sx={{ position: 'absolute', right: 48, top: 8 }}>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                aria-label="refresh entities"
                onClick={handleRefresh}
                disabled={isFetching}
                size="small"
              >
                <RefreshIcon
                  fontSize="small"
                  sx={
                    isFetching
                      ? {
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' },
                          },
                        }
                      : undefined
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="entity-type-filter-label">Entity Type</InputLabel>
              <Select
                labelId="entity-type-filter-label"
                label="Entity Type"
                value={filters.entityType}
                onChange={(e) => handleFilterChange('entityType', e.target.value)}
                MenuProps={SELECT_MENU_PROPS}
                renderValue={(selected) => {
                  const label =
                    selected === ALL_ENTITY_TYPE_FILTER
                      ? 'All'
                      : formatEntityTypeLabel(selected);
                  return (
                    <Typography noWrap title={label}>
                      {label}
                    </Typography>
                  );
                }}
              >
                <MenuItem value={ALL_ENTITY_TYPE_FILTER}>All</MenuItem>
                {entityTypeOptions.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Typography noWrap title={formatEntityTypeLabel(type)}>
                      {formatEntityTypeLabel(type)}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Account Status</InputLabel>
              <Select
                label="Account Status"
                value={filters.accountStatus}
                onChange={(e) => handleFilterChange('accountStatus', e.target.value)}
              >
                {ACCOUNT_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                label="Sort By"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                {SORT_BY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                label="Order"
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <MenuItem value="DESC">Newest First</MenuItem>
                <MenuItem value="ASC">Oldest First</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Stripe Customer</InputLabel>
              <Select
                label="Stripe Customer"
                value={filters.hasStripeCustomer}
                onChange={(e) => handleFilterChange('hasStripeCustomer', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Connect Account</InputLabel>
              <Select
                label="Connect Account"
                value={filters.hasStripeAccount}
                onChange={(e) => handleFilterChange('hasStripeAccount', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}
        </Grid>

        <TableContainer>
          <Table stickyHeader size="small">
            <CustomTableHead>
              <TableRow>
                {columns.map((col) => (
                  <CustomTableHeadCell key={col.id} align={col.align} sx={{ minWidth: col.minWidth }}>
                    {col.label}
                  </CustomTableHeadCell>
                ))}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No entities found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <CustomTableRow key={row.entityId} hover>
                    <TableCell>{row.name || '-'}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell align="center">{row.type}</TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={getStatusColor(row.accountStatus, theme)}
                      >
                        {formatStatusLabel(row.accountStatus)}
                        {canRequestVerification(row.accountStatus) && (
                          <Tooltip title="Request verification">
                            <span>
                              <IconButton
                                size="small"
                                disabled={verificationLoading}
                                onClick={() => handleRequestVerification(row.entityId)}
                              >
                                <SyncProblemOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Typography>
                    </TableCell>
                    {/* <TableCell align="center">{row.externalMasterId || '-'}</TableCell> */}
                    <TableCell align="center">
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '-'}
                    </TableCell>
                  </CustomTableRow>
                ))
              )}
            </TableBody>
            <CustomTableFoot>
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ border: 0, p: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <CustomTablePagination
                      page={pagination.page}
                      rowsPerPage={pagination.rowsPerPage}
                      count={data?.pagination?.total ?? 0}
                      onNewPage={(newPage, rowsPerPage) =>
                        setPagination({ page: newPage, rowsPerPage })
                      }
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </CustomTableFoot>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardedEntitiesDialog;
